import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal, viewChild } from '@angular/core';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, of, Subscription, switchMap, tap } from 'rxjs';
import { EditorModule } from 'primeng/editor';
import { FormFooterComponent, FormFooterStepConfig, SubmitState } from 'src/app/shared/components/form-footer/form-footer.component';
import { MultiSelectChipFieldComponent, MultiSelectOption } from 'src/app/shared/components/multi-select-chip-field/multi-select-chip-field.component';
import { DocumentationLinksTableComponent } from 'src/app/shared/components/documentation-links-table/documentation-links-table.component';
import { AlternativeToolsTableComponent } from 'src/app/shared/components/alternative-tools-table/alternative-tools-table.component';
import { ContactsTableComponent } from 'src/app/shared/components/contacts-table/contacts-table.component';
import { ImageSelectorComponent } from 'src/app/shared/components/image-selector/image-selector.component';
import { LifeCyclePhase } from '../../models/life-cycle-phase.enum';
import { AppConfig } from 'src/app/app-config.service';
import { PersonaService } from 'src/app/shared/services/persona.service';
import { ToolTypeService } from 'src/app/shared/services/tool-type.service';
import { ToolService } from '../../services/tool.service';
import { ToolUpsertRequest } from '../../models/create-tool-request.model';
import { DialogHelper } from 'src/app/shared/utilities/helpers/dialog-helper/dialog-helper';
import { companyPortalOrHttpUrlValidator } from 'src/app/shared/validators/form-validators';
import { CONTACT_TYPE_ID_MAP } from 'src/app/shared/utilities/mappers/contact-type.mappers';
import { ContactType, LaunchType, LAUNCH_TYPE_OPTIONS, CMDB_CONTACT_TYPES } from '../../models/tool-form-types';
import { buildCreateToolPayload } from '../../mappers/create-tool.mapper';
import { ToolStatus } from '../../models/tool-status.enum';
import { normalizeNbsp } from 'src/app/shared/utilities/helpers/string-helpers';
import { Tool } from '../../models/tool.model';
import { RelationshipType } from '../../models/tool-form-types';
import { v4 as uuidv4 } from 'uuid';
import { ServiceNowCmdbService } from 'src/app/shared/services/service-now-cmdb.service';
import { CmdbApplicationDetails } from 'src/app/shared/models/service-now/cmdb-application.model';
import { ToolValidationIssue } from '../../models/tool-validation-issues.model';
import { processBackendError } from 'src/app/shared/utilities/helpers/error-formatter';
import { AlternativeToolForm, createAlternativeToolGroup, createContactGroup, createDocumentationLinkGroup, ResourceUrlForm, ToolContactForm, applyContactDetailValidators } from 'src/app/shared/utilities/helpers/tool-form.forms';

interface CmdbSuggestion {
  trackKey: string;
  label: string;
  name: string;
  appId: string;
  details?: CmdbApplicationDetails;
}

export interface ToolFormDialogResult {
  created?: boolean;
  createdToolId?: number;
  updated?: boolean;
  updatedToolId?: number;
}

interface ToolFormDialogData {
  tool?: Tool;
  uploadSessionKey?: string;
}

@Component({
  selector: 'app-tools-form-modal',
  templateUrl: './tools-form-modal.component.html',
  styleUrls: ['./tools-form-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(window:beforeunload)': 'onBeforeUnload($event)' },
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    EditorModule,
    FormFooterComponent,
    MultiSelectChipFieldComponent,
    DocumentationLinksTableComponent,
    AlternativeToolsTableComponent,
    ContactsTableComponent,
    ImageSelectorComponent,
  ],
})
export class ToolsFormModalComponent {
  // Dependencies
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef = inject(MatDialogRef<ToolsFormModalComponent, ToolFormDialogResult>);
  private readonly dialogHelper = inject(DialogHelper);
  private readonly toolService = inject(ToolService);
  private readonly personaService = inject(PersonaService);
  private readonly toolTypeService = inject(ToolTypeService);
  private readonly serviceNowCmdbService = inject(ServiceNowCmdbService);
  private readonly destroyRef = inject(DestroyRef);

  // Dialog data
  readonly data = inject<ToolFormDialogData>(MAT_DIALOG_DATA, { optional: true }) ?? {};
  readonly isEditMode = computed(() => !!this.data.tool);

  // Data sources
  readonly personas = this.personaService.personas;
  readonly toolTypes = this.toolTypeService.toolTypes;

  readonly toolOptions = signal<Array<{ id: number; name: string }>>([]);
  readonly toolOptionsLoading = signal(false);
  readonly toolOptionsError = signal<string | null>(null);

  // Edit mode state
  readonly isLoadingTool = signal(false);
  readonly existingTool = signal<Tool | null>(null);

  // CMDB autocomplete state
  readonly cmdbLoadCount = signal(0);
  readonly cmdbContactsLoading = computed(() => this.cmdbLoadCount() > 0);
  readonly serviceIdSuggestions = signal<CmdbSuggestion[]>([]);
  private cmdbAutocompleteSub: Subscription | null = null;
  private lastAppliedServiceId: string | null = null;

  // ---------- Options
  readonly lifeCyclePhaseOptions: { id: LifeCyclePhase; name: string }[] = Object
    .values(LifeCyclePhase)
    .filter((v): v is number => typeof v === 'number' && v !== LifeCyclePhase.None)
    .map(id => ({ id, name: LifeCyclePhase[id] as string }));

  readonly launchTypeOptions = LAUNCH_TYPE_OPTIONS;

  // ---------- UI state
  readonly closeAriaLabel = computed(() => this.isEditMode() ? 'Close edit tool dialog' : 'Close create tool dialog');
  readonly submitButtonLabel = computed(() => this.isEditMode() ? 'save' : 'submit');

  readonly isSubmitting = signal(false);
  readonly submitError = signal<string | null>(null);
  readonly submitIssues = signal<ToolValidationIssue[]>([]);

  readonly fileError = signal<string | null>(null);

  readonly isUploadingPreview = signal(false);
  readonly uploadingGalleryCount = signal(0);
  readonly isUploadingAny = computed(() => this.isUploadingPreview() || this.uploadingGalleryCount() > 0);

  // Track image references (set via component events)
  readonly previewReference = signal<string | null>(null);

  // Upload session key for temporary blob storage.
  // Can be passed in or generated.
  readonly uploadSessionKey: string;

  // Form validity signals (populated via effect in constructor)
  readonly step1Valid = signal(false);
  readonly step2Valid = signal(false);
  readonly step3Valid = signal(false);

  // ---------- Forms
  readonly step1 = this.fb.group({
    name: this.fb.nonNullable.control('', [Validators.required, Validators.maxLength(200)]),
    serviceId: this.fb.nonNullable.control('', [Validators.maxLength(200)]),
    url: this.fb.nonNullable.control('', [Validators.required, companyPortalOrHttpUrlValidator()]),
    status: this.fb.nonNullable.control<ToolStatus>(ToolStatus.Active, [Validators.required]),
    launchType: this.fb.nonNullable.control<LaunchType | ''>('', [Validators.required]),
    shortDescription: this.fb.nonNullable.control('', [Validators.maxLength(500)]),
    description: this.fb.nonNullable.control(''),
    previewImage: this.fb.control<File | null>(null),
  });

  readonly step2 = this.fb.group({
    personaIds: this.fb.nonNullable.control<number[]>([]),
    lifeCyclePhaseIds: this.fb.nonNullable.control<LifeCyclePhase[]>([]),
    toolTypeIds: this.fb.nonNullable.control<number[]>([]),
    specialCondition: this.fb.nonNullable.control(''),
    galleryImages: this.fb.control<File[]>([]),
  });

  readonly step3 = this.fb.group({
    documentationLinks: this.fb.array<FormGroup<ResourceUrlForm>>([]),
    alternativeTools: this.fb.array<FormGroup<AlternativeToolForm>>([]),
    contacts: this.fb.array<FormGroup<ToolContactForm>>([]),
  });

  readonly form = this.fb.group({
    step1: this.step1,
    step2: this.step2,
    step3: this.step3,
  });

  readonly modalTitle = computed(() => this.isEditMode() ? 'Edit Tool' : 'Create Tool');

  // ---------- Reactive tracking (toSignal)
  private readonly step1Status = toSignal(this.step1.statusChanges, { initialValue: this.step1.status });
  private readonly step2Status = toSignal(this.step2.statusChanges, { initialValue: this.step2.status });
  private readonly step3Status = toSignal(this.step3.statusChanges, { initialValue: this.step3.status });

  // Convert selection values to signals so computed properties recompute reactively.
  private readonly nameValue = toSignal(this.step1.controls.name.valueChanges, { initialValue: this.step1.controls.name.value });
  private readonly urlValue = toSignal(this.step1.controls.url.valueChanges, { initialValue: this.step1.controls.url.value });
  private readonly launchTypeValue = toSignal(this.step1.controls.launchType.valueChanges, { initialValue: this.step1.controls.launchType.value });
  private readonly shortDescriptionValue = toSignal(this.step1.controls.shortDescription.valueChanges, { initialValue: this.step1.controls.shortDescription.value });

  // ---------- View-model (derived)
  readonly shortDescriptionCount = computed(() => String(this.shortDescriptionValue() ?? '').length);

  readonly toolStatusOptions = Object.values(ToolStatus) as ToolStatus[];

  readonly stepConfigs = computed<FormFooterStepConfig[]>(() => [
    { label: 'Details', isValid: () => this.step1Valid() },
    { label: 'Modal', isValid: () => this.step2Valid() },
    { label: 'Additional', isValid: () => this.step3Valid() }
  ]);

  readonly requiredFieldIssues = computed(() => {
    this.step1Status();
    this.nameValue();
    this.urlValue();
    this.launchTypeValue();

    const issues: string[] = [];

    const name = this.step1.controls.name;
    const url = this.step1.controls.url;
    const launchType = this.step1.controls.launchType;

    if (!String(name.value ?? '').trim()) {
      issues.push('Tool name');
    } else if (name.hasError('maxlength')) {
      issues.push('Tool name (max 200 characters)');
    }

    if (!String(url.value ?? '').trim()) {
      issues.push('Link');
    } else if (url.hasError('url')) {
      issues.push('Link (enter a valid URL)');
    }

    if (!String(launchType.value ?? '').trim()) {
      issues.push('Launch type');
    }

    return issues;
  });

  readonly submitState = computed<SubmitState>(() => {
    if (this.isSubmitting()) return 'submitting';
    if (this.isUploadingAny() || this.cmdbContactsLoading() || !this.step1Valid()) return 'disabled';
    return 'enabled';
  });

  readonly submitDisabledTooltip = computed(() => {
    if (this.isSubmitting() || this.submitState() === 'enabled') return '';

    if (this.isUploadingAny()) {
      return 'Uploading images... please wait.';
    }

    if (this.cmdbContactsLoading()) {
      return 'Loading CMDB contacts... please wait.';
    }

    const issues = this.requiredFieldIssues();
    if (!issues.length) return 'Please complete the required fields before submitting.';
    return `Complete before submitting:\n• ${issues.join('\n• ')}`;
  });

  // Multi-select options for reusable component
  readonly personaOptions = computed<MultiSelectOption[]>(() =>
    this.personas().map(p => ({ id: p.id, name: p.name }))
  );
  readonly toolTypeOptions = computed<MultiSelectOption[]>(() =>
    this.toolTypes().map(t => ({ id: t.id, name: t.name }))
  );
  readonly lifecycleOptions = computed<MultiSelectOption[]>(() =>
    this.lifeCyclePhaseOptions.map(l => ({ id: l.id, name: l.name }))
  );

  // ---------- ViewChild refs
  readonly stepper = viewChild<MatStepper>('stepper');
  readonly formFooter = viewChild<FormFooterComponent>('formFooter');
  readonly previewImageSelector = viewChild<ImageSelectorComponent>('previewImageSelector');
  readonly galleryImageSelector = viewChild<ImageSelectorComponent>('galleryImageSelector');
  readonly contactsTable = viewChild<ContactsTableComponent>('contactsTable');

  constructor() {
    const sessionKeyCandidate = (this.data.uploadSessionKey ?? '').trim();
    this.uploadSessionKey = sessionKeyCandidate || uuidv4();

    // Do not close on backdrop click / ESC
    this.dialogRef.disableClose = true;

    this.personaService.loadPersonas();
    this.toolTypeService.loadToolTypes();

    this.loadToolOptions();

    // Setup CMDB autocomplete
    this.setupCmdbAutocomplete();

    // Track form validity using effect - read status signals to be reactive
    effect(() => {
      this.step1Status(); // Read signals to track changes
      this.step2Status();
      this.step3Status();

      this.step1Valid.set(this.step1.valid);
      this.step2Valid.set(this.step2.valid);
      this.step3Valid.set(this.step3.valid);
    }, { allowSignalWrites: true });

    if (this.data.tool) {
      this.existingTool.set(this.data.tool);
      this.isLoadingTool.set(true);
    } 
    else {
      this.documentationLinks.push(createDocumentationLinkGroup(this.fb));
      this.alternativeTools.push(createAlternativeToolGroup(this.fb));
      this.contacts.push(createContactGroup(this.fb));
    }

  }

  ngAfterViewInit(): void {
    if (this.isLoadingTool() && this.existingTool()) {
      this.populateFormWithTool(this.existingTool()!);
      this.isLoadingTool.set(false);
    }

    this.destroyRef.onDestroy(() => {
      this.cmdbAutocompleteSub?.unsubscribe();
    });
  }

  onServiceIdSelected(selected: unknown): void {
    const s = selected as CmdbSuggestion | null;
    if (!s) return;

    this.applyCmdbSuggestion(s);
  }

  onServiceIdBlur(): void {
    const term = String(this.step1.controls.serviceId.value ?? '').trim();
    if (!term) return;

    const match = this.findExactCmdbMatch(term);
    if (match) {
      this.applyCmdbSuggestion(match);
    }
  }

  onServiceIdEnter(event: Event): void {
    // While Service ID is focused, Enter should pick the first suggestion
    // rather than submit the form.
    const keyboardEvent = event as KeyboardEvent;
    keyboardEvent.preventDefault();
    keyboardEvent.stopPropagation();

    const suggestions = this.serviceIdSuggestions();
    if (suggestions.length > 0) {
      this.applyCmdbSuggestion(suggestions[0]!);
      return;
    }

    this.onServiceIdBlur();
  }

  onStepperSelectionChange(event: StepperSelectionEvent): void {
    const footer = this.formFooter();
    if (footer) {
      footer.goToStep(event.selectedIndex);
    }
  }

  onStepChange(targetStep: number): void {
    // Mark previous step as touched when moving forward to show validation errors
    if (targetStep === 1) this.step1.markAllAsTouched();
    if (targetStep === 2) this.step2.markAllAsTouched();
    
    const footer = this.formFooter();
    if (footer) {
      footer.goToStep(targetStep);
    }

    const stepper = this.stepper();
    if (stepper) {
      stepper.selectedIndex = targetStep;
    }
  }

  // ---------- FormArray helpers

  get documentationLinks(): FormArray<FormGroup<ResourceUrlForm>> {
    return this.step3.controls.documentationLinks;
  }

  get alternativeTools(): FormArray<FormGroup<AlternativeToolForm>> {
    return this.step3.controls.alternativeTools;
  }

  get contacts(): FormArray<FormGroup<ToolContactForm>> {
    return this.step3.controls.contacts;
  }

  // ---------- Files (handled by image picker components)

  close(): void {
    if (this.isSubmitting()) return;

    if (!this.form.dirty) {
      this.dialogRef.close({ created: false, updated: false });
      return;
    }

    const title = this.isEditMode() ? 'Edit Tool' : 'Create Tool';
    const message = this.isEditMode()
      ? 'Closing modal will discard the current changes. Are you sure?'
      : 'Closing modal will discard the current changes. Save as draft before closing?';

    this.dialogHelper.openConfirmationDialog({
      title,
      message,
      confirmText: this.isEditMode() ? 'discard changes' : 'save draft and close',
      cancelText: this.isEditMode() ? 'cancel' : 'discard changes',
      type: 'confirm'
    }).subscribe((confirmed) => {
      if (this.isEditMode()) {
        if (confirmed) {
          this.dialogRef.close({ created: false, updated: false });
        }
      } else {
        if (confirmed) {
          void this.saveDraftAndClose();
        } else {
          this.dialogRef.close({ created: false, updated: false });
        }
      }
    });
  }

  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.form.dirty) return;
    event.preventDefault();
  }

  submit(): void {
    this.submitError.set(null);
    this.submitIssues.set([]);
    this.form.markAllAsTouched();

    if (this.isUploadingAny()) {
      this.submitError.set('Please wait for images to finish uploading before submitting.' );
      return;
    }

    if (this.step1.invalid || this.step2.invalid) {
      this.submitError.set('Please fix validation errors before submitting.');
      return;
    }

    if (this.step3.invalid) {
      this.submitError.set('Please fix errors in the additional sections before submitting.');
      return;
    }

    this.isSubmitting.set(true);

    const payload = this.buildCreatePayload();
    const toolId = this.data.tool?.id;

    const request$ = toolId
      ? this.toolService.updateTool(toolId, payload)
      : this.toolService.createTool(payload);

    request$.subscribe({
      next: (result) => {
        const resultId = typeof (result as any)?.id === 'number' ? (result as any).id : undefined;
        if (toolId) {
          this.dialogRef.close({ updated: true, updatedToolId: resultId ?? toolId });
        } else {
          this.dialogRef.close({ created: true, createdToolId: resultId });
        }
      },
      error: (err) => {
        const action = toolId ? 'update' : 'create';
        console.error(`Error ${action}ing tool:`, err);
        const { message, issues } = processBackendError(err, `Unable to ${action} tool. Please try again.`);
        this.submitError.set(message);
        this.submitIssues.set(issues);
        this.isSubmitting.set(false);
      },
      complete: () => {
        this.isSubmitting.set(false);
      }
    });
  }

  // ---------- Private helpers (grouped)

  // --- Draft helpers

  private missingRequiredFieldsForDraft(): string[] {
    const name = String(this.step1.controls.name.value ?? '').trim();
    const url = String(this.step1.controls.url.value ?? '').trim();
    const launchType = String(this.step1.controls.launchType.value ?? '').trim();

    const missing: string[] = [];
    if (!name) missing.push('Tool name');
    if (!url) missing.push('Link');
    if (!launchType) missing.push('Launch type');
    return missing;
  }

  private buildDraftPayload(): ToolUpsertRequest {
    return {
      ...this.buildCreatePayload(),
      status: ToolStatus.Draft,
    };
  }

  private saveDraftAndClose(): void {
    if (this.isSubmitting()) return;

    this.submitError.set(null);
    this.submitIssues.set([]);

    if (this.isUploadingAny()) {
      this.submitError.set('Please wait for images to finish uploading before saving.');
      return;
    }

    const missing = this.missingRequiredFieldsForDraft();
    if (missing.length) {
      this.submitError.set(`Please complete required fields before saving as draft: ${missing.join(', ')}.`);
      this.step1.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);

    const payload = this.buildDraftPayload();

    this.toolService.createTool(payload).subscribe({
      next: (created) => {
        const createdToolId = typeof (created as any)?.id === 'number' ? (created as any).id : undefined;
        this.dialogRef.close({ created: true, createdToolId });
      },
      error: (err) => {
        console.error('Error saving tool as draft:', err);
        const { message, issues } = processBackendError(err, 'Unable to save as draft. Please try again.');
        this.submitError.set(message);
        this.submitIssues.set(issues);
        this.isSubmitting.set(false);
      },
      complete: () => {
        this.isSubmitting.set(false);
      }
    });
  }

  // --- Options loading

  private loadToolOptions(): void {
    this.toolOptionsLoading.set(true);
    this.toolOptionsError.set(null);

    this.toolService.getToolNames().subscribe({
      next: (options) => {
        this.toolOptions.set([...options].sort((a, b) => a.name.localeCompare(b.name)));
      },
      error: (err) => {
        console.error('Error loading tool options:', err);
        this.toolOptionsError.set('Unable to load tools for selection.');
        this.toolOptions.set([]);
      },
      complete: () => {
        this.toolOptionsLoading.set(false);
      }
    });
  }

  private populateFormWithTool(tool: Tool): void {
    this.step1.patchValue({
      name: tool.name ?? '',
      serviceId: tool.serviceId != null ? String(tool.serviceId) : '',
      url: tool.url ?? '',
      status: tool.status ?? ToolStatus.Active,
      launchType: tool.launchType?.trim().replace(' ', '') as LaunchType ?? '',
      shortDescription: tool.shortDescription ?? '',
      description: normalizeNbsp(tool.description ?? ''),
      previewImage: null,
    });

    const personaNameToId = new Map(this.personas().map(p => [p.name.toLowerCase(), p.id]));
    const toolTypeNameToId = new Map(this.toolTypes().map(t => [t.name.toLowerCase(), t.id]));

    const personaIds = (tool.personas ?? [])
      .map(n => personaNameToId.get(String(n ?? '').toLowerCase()))
      .filter((v): v is number => typeof v === 'number');

    const toolTypeIds = (tool.toolTypes ?? [])
      .map(n => toolTypeNameToId.get(String(n ?? '').toLowerCase()))
      .filter((v): v is number => typeof v === 'number');

    const lifeCyclePhaseIds = (tool.lifeCyclePhases ?? [])
      .map(n => this.lifeCyclePhaseFromName(n))
      .filter((v): v is LifeCyclePhase => v !== null);

    this.step2.patchValue({
      personaIds,
      toolTypeIds,
      lifeCyclePhaseIds,
      specialCondition: normalizeNbsp(tool.specialCondition ?? ''),
      galleryImages: [],
    });

    const id = tool.id;
    const previewRef = String(tool.previewReference ?? '').trim();
    if (previewRef) {
      this.previewReference.set(previewRef);
      const previewUrl = `${AppConfig.settings.cdnUrl}/tools-preview/${id}/${previewRef}`;
      this.previewImageSelector()?.previews.set([
        { key: previewRef, name: previewRef, reference: previewRef, url: previewUrl, kind: 'existing' }
      ] as any);
    }

    const galleryRefs = tool.galleryReferences ?? [];
    if (galleryRefs.length) {
      const galleryPreviews = galleryRefs.map(ref => ({
        key: ref,
        name: ref,
        reference: ref,
        url: `${AppConfig.settings.cdnUrl}/tools-gallery/${id}/${ref}`,
        kind: 'existing'
      }));
      this.galleryImageSelector()?.previews.set(galleryPreviews as any);
    }

    this.documentationLinks.clear();
    this.alternativeTools.clear();
    this.contacts.clear();

    const docs = tool.documentationLinks ?? [];
    if (docs.length) {
      for (const d of docs) {
        const g = createDocumentationLinkGroup(this.fb);
        g.patchValue({
          title: String(d?.title ?? ''),
          url: String(d?.url ?? ''),
          description: String(d?.description ?? ''),
        });
        this.documentationLinks.push(g);
      }
    } else {
      this.documentationLinks.push(createDocumentationLinkGroup(this.fb));
    }

    const alts = tool.alternativeTools ?? [];
    if (alts.length) {
      for (const a of alts) {
        const g = createAlternativeToolGroup(this.fb);
        g.patchValue({
          relatedToolId: typeof a?.id === 'number' ? a.id : null,
          relationshipType: (String(a?.relationshipType ?? 'Alternative') as RelationshipType),
        });
        this.alternativeTools.push(g);
      }
    } else {
      this.alternativeTools.push(createAlternativeToolGroup(this.fb));
    }

    const contactsList = tool.contacts ?? [];
    if (contactsList.length) {
      for (const contact of contactsList) {
        const contactTypeEntry = Object.entries(CONTACT_TYPE_ID_MAP).find(([_, id]) => id === contact?.type);
        const mappedType = (contactTypeEntry?.[0] as ContactType) ?? ContactType.Owner;
        if (CMDB_CONTACT_TYPES.includes(mappedType)) continue;

        const g = createContactGroup(this.fb);
        g.patchValue({
          type: mappedType,
          name: String(contact?.name ?? ''),
          contactDetail: String(contact?.contactDetail ?? ''),
        });

        applyContactDetailValidators(g);
        this.contacts.push(g);
      }
    } else {
      this.contacts.push(createContactGroup(this.fb));
    }

    const loadedServiceId = String(tool.serviceId ?? '').trim();
    if (loadedServiceId) {
      this.lastAppliedServiceId = loadedServiceId;
      this.cmdbLoadCount.update(c => c + 1);
      this.serviceNowCmdbService.getApplicationById(loadedServiceId).pipe(
        catchError(() => of(null)),
        finalize(() => this.cmdbLoadCount.update(c => Math.max(0, c - 1)))
      ).subscribe((details) => {
        if (details) this.applyContacts(details);
      });
    }

    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  private lifeCyclePhaseFromName(name: string): LifeCyclePhase | null {
    const raw = String(name ?? '').trim();
    if (!raw) return null;

    const matchKey = Object.keys(LifeCyclePhase)
      .find(k => Number.isNaN(Number(k)) && k.toLowerCase() === raw.toLowerCase());

    if (!matchKey) return null;
    const value = (LifeCyclePhase as Record<string, unknown>)[matchKey];
    return typeof value === 'number' ? (value as LifeCyclePhase) : null;
  }

  // --- File helpers (handled by image picker components)

  // --- Payload building

  private buildCreatePayload(): ToolUpsertRequest {
    const step1 = this.step1.getRawValue();
    const step2Raw = this.step2.getRawValue();
    const step2 = {
      ...step2Raw,
      galleryImages: step2Raw.galleryImages ?? [],
    };
    const documentationLinks = this.documentationLinks.controls.map(c => c.getRawValue());
    const alternativeTools = this.alternativeTools.controls.map(c => c.getRawValue());
    const contacts = this.contacts.controls.map(c => c.getRawValue());

    const previewReferenceOverride = this.previewReference() ?? undefined;

    const galleryReferencesOverride = this.galleryImageSelector()?.getReferencesList() ?? [];

    return buildCreateToolPayload({
      step1,
      step2,
      documentationLinks,
      alternativeTools,
      contacts,
      contactTypeIdMap: CONTACT_TYPE_ID_MAP,
      temporaryBlobId: this.uploadSessionKey,
      previewReferenceOverride,
      galleryReferencesOverride: galleryReferencesOverride.length ? galleryReferencesOverride : undefined,
    });
  }

  // ========== CMDB Contact Hydration ==========

  private setupCmdbAutocomplete(): void {
    this.cmdbAutocompleteSub?.unsubscribe();

    this.cmdbAutocompleteSub = this.step1.controls.serviceId.valueChanges
      .pipe(
        map((v) => String(v ?? '').trim()),
        tap((term) => {
          if (this.lastAppliedServiceId && term !== this.lastAppliedServiceId) {
            this.lastAppliedServiceId = null;
            this.clearCmdbContacts();
          }
        }),
        debounceTime(800),
        distinctUntilChanged(),
        switchMap((term) => {
          if (!term) return of([] as CmdbSuggestion[]);

          this.cmdbLoadCount.update(c => c + 1);

          const isNumericId = /^\d+$/.test(term);
          const request$ = isNumericId
            ? this.serviceNowCmdbService.getApplicationById(term).pipe(
                map((d) => d ? [{ trackKey: `id:${term}`, label: d.appId ? `${d.name} (${d.appId})` : d.name, name: d.name, appId: d.appId, details: d }] : [])
              )
            : this.serviceNowCmdbService.searchApplications(term).pipe(
                map((items) => (items ?? []).map((x) => ({ trackKey: `search:${term}`, label: x.appId ? `${x.name} (${x.appId})` : x.name, name: x.name, appId: x.appId })))
              );

          return request$.pipe(
            catchError(() => of([] as CmdbSuggestion[])),
            finalize(() => this.cmdbLoadCount.update(c => Math.max(0, c - 1))),
          );
        }),
      )
      .subscribe((items) => this.serviceIdSuggestions.set(items));
  }

  private applyCmdbSuggestion(suggestion: CmdbSuggestion): void {
    const serviceId = String(suggestion.appId ?? '').trim();
    if (this.lastAppliedServiceId && this.lastAppliedServiceId !== serviceId) {
      this.clearCmdbContacts();
    }
    this.lastAppliedServiceId = serviceId || null;

    if (serviceId) this.step1.controls.serviceId.setValue(serviceId, { emitEvent: false });
    this.step1.controls.name.setValue(suggestion.name, { emitEvent: false });
    this.serviceIdSuggestions.set([]);

    if (suggestion.details) {
      this.applyContacts(suggestion.details);
    } else if (serviceId) {
      this.cmdbLoadCount.update(c => c + 1);
      this.serviceNowCmdbService.getApplicationById(serviceId).pipe(
        catchError(() => of(null)),
        finalize(() => this.cmdbLoadCount.update(c => Math.max(0, c - 1))),
      ).subscribe((details) => details && this.applyContacts(details));
    }
  }

  private findExactCmdbMatch(term: string): CmdbSuggestion | null {
    const normalized = term.trim().toLowerCase();
    if (!normalized) return null;

    return this.serviceIdSuggestions().find((s) => {
      const lower = (v: string | undefined) => String(v ?? '').trim().toLowerCase();
      return lower(s.appId) === normalized || lower(s.name) === normalized || lower(s.label) === normalized;
    }) ?? null;
  }

  private applyContacts(details: CmdbApplicationDetails): void {
    const table = this.contactsTable();
    if (!table) return;

    const upsert = (type: ContactType, contact: { name?: string; email?: string } | null | undefined) => {
      if (!contact) return;
      const name = String(contact.name ?? '').trim();
      const email = String(contact.email ?? '').trim();
      if (name || email) table.upsertCmdbContact(type, name, email);
    };

    upsert(ContactType.ServiceNowBusinessOwner, details.businessOwner);
    upsert(ContactType.ServiceNowTechnicalOwner, details.technicalOwner);
    upsert(ContactType.ServiceNowSupportGroup, details.supportGroup);

    table.sortContactsByCmdbFirst();
  }

  private clearCmdbContacts(): void {
    this.contactsTable()?.clearCmdbContacts();
  }
}

